import { PageHeader } from "@bednbody/ui/components/page-header";
import { Empty } from "@bednbody/ui/components/empty";
import { Button } from "@bednbody/ui/components/button";
import { ChartBarIcon } from "@phosphor-icons/react";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Badge } from "@bednbody/ui/components/badge";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Callout } from "@bednbody/ui/components/callout";
import { useState } from "react";
import { isReservedAwaitingWindow } from "@bednbody/logic";
import { cn } from "@bednbody/ui/lib/utils";
import { expectedServiceEnd } from "@bednbody/logic";
import {
  ArrowRightIcon,
  MapPinIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { EdgeFunctionError } from "@/lib/edge-functions";
import { useAuth } from "@/lib/auth";
import { isHubScoped } from "@/lib/jwt-claims";
import { bookingStatusLabel } from "@/lib/bookingStatusLabels";
import { minutesSince } from "@/lib/time";
import { EMPTY_FILTERS, useOpsBookings } from "../ops-dashboard/ops-dashboard.hooks";
import {
  useAssignDispatch,
  useAssignSpecificRider,
  useReleaseRiderDispatch,
  useRiderQueue,
  useTherapistActiveBookings,
  useTherapistQueue,
  useTherapistsAwaitingPickup,
} from "./dispatch.hooks";
import { ReleaseDispatchModal } from "./ReleaseDispatchModal";

const QUEUE_STATUS: Record<string, { label: string; badge: string; dot: string }> = {
  available: { label: "Available", badge: "bg-success/15 text-success", dot: "bg-success" },
  assigned: { label: "On trip", badge: "bg-info/15 text-info", dot: "bg-info" },
  offline: {
    label: "Offline",
    badge: "bg-muted text-muted-foreground",
    dot: "bg-muted-foreground/50",
  },
};

/** Booking states 0053 refuses to release a rider from — he is already on the way or there. */
const RELEASE_REFUSED = new Set(["en_route_to_client", "arrived"]);

/**
 * Minutes left on a therapist's current job — `expectedServiceEnd` (CORE-01B
 * §4.4's "estimated availability") folds her actual phase in: running Service
 * timer if there is one, the Confirm Drop-Off countdown if she just arrived,
 * or a earliest-possible-finish estimate if she is still en route. Same
 * function the allocation engine ranks Engaged therapists by, so the number
 * shown here is never a second, drifting guess.
 */
function minutesLeftOnJob(row: {
  scheduled_at: string;
  service_start_deadline_at: string | null;
  service_ends_at: string | null;
  service_variants: { duration_minutes: number } | { duration_minutes: number }[] | null;
}): number | null {
  const variant = Array.isArray(row.service_variants)
    ? row.service_variants[0]
    : row.service_variants;
  if (!variant) return null;
  const endsAt = expectedServiceEnd(
    {
      scheduledAt: row.scheduled_at,
      durationMinutes: variant.duration_minutes,
      serviceStartDeadlineAt: row.service_start_deadline_at,
      serviceEndsAt: row.service_ends_at,
    },
    new Date().toISOString(),
  );
  return Math.max(0, Math.ceil((Date.parse(endsAt) - Date.now()) / 60_000));
}

const TIER_LABEL: Record<string, string> = {
  new_therapist: "New — first duty day",
  rested_yesterday: "Rested yesterday",
  approved_leave: "Approved leave",
  worked_yesterday: "Worked yesterday",
  late_online: "Late online",
  absent_yesterday: "Absent yesterday",
};

/**
 * Dispatch — the rider FIFO and the bookings stuck without one.
 * "Assign next rider" is the FIFO pick (POST /dispatch/assign,
 * service_role-only assign_rider, SKIP LOCKED). "Assign this rider" is the
 * manual override (POST /dispatch/assign-rider, 0053) for when the queue
 * pick isn't the right call. The × on an "On trip" row RELEASES that rider
 * (POST /dispatch/riders/:id/release, 0053) — the booking is not cancelled, it
 * goes back to waiting for a rider — both
 * overrides are pre-pickup only; once a rider has a live trip, use the
 * booking's own status override instead. Live GPS lives on /live-map; this
 * page is queue truth.
 */
export function DispatchPage() {
  const { claims } = useAuth();
  const hubScoped = claims ? isHubScoped(claims.role) : false;
  const hubId = hubScoped ? (claims?.hub_id ?? null) : null;

  const { data: queue, isLoading, isError } = useRiderQueue(hubId);
  const { data: therapistQueue, isLoading: therapistQueueLoading } = useTherapistQueue(hubId);
  const { data: therapistActiveBookings } = useTherapistActiveBookings(hubId);
  const activeBookingByTherapist = new Map(
    (therapistActiveBookings ?? []).map((row) => [row.therapist_id as string, row]),
  );
  const { data: awaitingRiderRaw, isLoading: awaitingLoading } = useOpsBookings({
    ...EMPTY_FILTERS,
    status: "awaiting_rider",
    hubId,
  });
  // A Reserve booking matched hours ahead of its own scheduled start is not
  // stuck — the system hasn't started looking for a rider yet, and won't
  // until the booking's own time arrives (mirrors `resolveTrackingStatus` on
  // the client and `resolvedBookingStatusStyle` on the therapist side; same
  // `isReservedAwaitingWindow` boundary, Leon's call 2026-09-23). Surfacing it
  // here as "stuck" reads as an incident that needs a manual assign, when
  // it's the normal, expected pre-window wait.
  const awaitingRider = awaitingRiderRaw?.filter(
    (b) => !isReservedAwaitingWindow(b.status, b.scheduled_at),
  );
  // Three real incidents in one afternoon (2026-09-23) were all this exact
  // leg — a therapist's pick-up (initial or Return) stuck `waiting_for_
  // pick_up` with no way for superadmin to even SEE it was happening short
  // of a rider or therapist saying so. Same hub scoping as the queues above.
  const { data: awaitingPickup, isLoading: pickupLoading } = useTherapistsAwaitingPickup(hubId);
  const assignDispatch = useAssignDispatch();
  const assignSpecificRider = useAssignSpecificRider();
  const releaseRiderDispatch = useReleaseRiderDispatch();
  const [specificRider, setSpecificRider] = useState<Record<string, string>>({});
  const [releaseTarget, setReleaseTarget] = useState<{ riderId: string; name: string } | null>(
    null,
  );

  const riders = queue ?? [];
  const available = riders.filter((r) => r.status === "available");
  const onTrip = riders.filter((r) => r.status === "assigned");
  const offline = riders.filter((r) => r.status === "offline");

  function confirmRelease(reason: string) {
    if (!releaseTarget) return;
    releaseRiderDispatch.mutate(
      { riderId: releaseTarget.riderId, reason },
      { onSuccess: () => setReleaseTarget(null) },
    );
  }

  const therapists = therapistQueue ?? [];
  // ⚠ LIVE-BUSY IS `prior_position`, NOT `moved_seq` (0036). `moved_seq` means
  // "an assignment moved them to the back of the queue at some point today", and
  // `asap_queue_complete` deliberately LEAVES IT SET — a finished session has
  // earned its place at the end. Only `asap_queue_restore` (cancel) clears it.
  // `prior_position` is the field the lifecycle actually closes: written on
  // assign, cleared by BOTH complete and restore, so non-null means assigned and
  // not yet finished. Reading `moved_seq` pinned a therapist at "On a job" for
  // the rest of the day after her first booking, with no live booking anywhere.
  const therapistsAssigned = therapists.filter((t) => t.prior_position !== null);

  return (
    <div className="flex min-w-0 flex-col gap-8">
      <PageHeader
        actions={
          <Link
            className="flex items-center gap-1.5 rounded-xl bg-primary/10 px-3 py-2 text-sm font-medium text-primary-dark transition-colors hover:bg-primary/15 dark:text-primary-light"
            to="/live-map"
          >
            <MapPinIcon size={15} /> Open live map <ArrowRightIcon size={13} />
          </Link>
        }
        description={
          hubScoped
            ? "Your hub's rider queue and anything stuck waiting for one."
            : "Rider queues across all hubs and anything stuck waiting for one."
        }
        title="Dispatch"
      />

      {isError && (
        <Callout tone="danger" role="alert">
          Couldn't load the rider queue — check your connection and try again.
        </Callout>
      )}

      {/* Stuck bookings — the intervention queue */}
      <section className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="type-title-subsection">Bookings awaiting a rider</h2>
          {awaitingRider && awaitingRider.length > 0 && (
            <Badge variant="warning-soft" shape="soft">
              {awaitingRider.length} stuck
            </Badge>
          )}
        </div>

        {assignDispatch.isError && (
          <p className="mb-3 text-sm text-destructive">
            {assignDispatch.error instanceof EdgeFunctionError &&
            assignDispatch.error.code === "NO_RIDER_AVAILABLE"
              ? "Still no rider available — the booking stays queued."
              : (assignDispatch.error as Error).message}
          </p>
        )}
        {assignSpecificRider.isError && (
          <p className="mb-3 text-sm text-destructive">
            {(assignSpecificRider.error as Error).message}
          </p>
        )}

        {awaitingLoading ? (
          <Skeleton className="h-16 motion-reduce:animate-none" />
        ) : !awaitingRider || awaitingRider.length === 0 ? (
          <Empty className="text-sm text-muted-foreground">
            No bookings stuck waiting for a rider.
          </Empty>
        ) : (
          <div className="space-y-2">
            {awaitingRider.map((b) => {
              const late = minutesSince(b.scheduled_at);
              const hubRiders = available.filter((r) => r.hub_id === b.hub_id);
              const chosenRider = specificRider[b.id] ?? "";
              return (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/30 bg-warning/5 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{b.ref}</p>
                    <p className="text-xs text-muted-foreground">
                      {b.hubs?.name ?? "Unknown hub"} · scheduled{" "}
                      {new Date(b.scheduled_at).toLocaleString("en-PH", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                      {late > 0 ? (
                        <span className="font-medium text-destructive"> · {late}m overdue</span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <NativeSelect
                      value={chosenRider}
                      onChange={(e) =>
                        setSpecificRider((prev) => ({ ...prev, [b.id]: e.target.value }))
                      }
                    >
                      <NativeSelectOption value="">Pick a rider…</NativeSelectOption>
                      {hubRiders.map((r) => (
                        <NativeSelectOption key={r.rider_id} value={r.rider_id}>
                          {r.staff?.full_name ?? "Rider"}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() =>
                        assignSpecificRider.mutate({ bookingId: b.id, riderId: chosenRider })
                      }
                      disabled={!chosenRider || assignSpecificRider.isPending}
                    >
                      {assignSpecificRider.isPending &&
                      assignSpecificRider.variables?.bookingId === b.id
                        ? "Assigning…"
                        : "Assign this rider"}
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => assignDispatch.mutate({ bookingId: b.id, hubId: b.hub_id })}
                      disabled={
                        assignDispatch.isPending && assignDispatch.variables?.bookingId === b.id
                      }
                    >
                      {assignDispatch.isPending && assignDispatch.variables?.bookingId === b.id
                        ? "Assigning…"
                        : "Assign next rider"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Therapists waiting to be collected — 2026-09-23, the leg behind
          today's three live incidents, surfaced here for the first time. */}
      <section className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="type-title-subsection">Therapists awaiting pickup</h2>
          {awaitingPickup && awaitingPickup.length > 0 && (
            <Badge variant="info-soft" shape="soft">
              {awaitingPickup.length} waiting
            </Badge>
          )}
        </div>
        {pickupLoading ? (
          <Skeleton className="h-16 motion-reduce:animate-none" />
        ) : !awaitingPickup || awaitingPickup.length === 0 ? (
          <Empty className="text-sm text-muted-foreground">
            No therapist is waiting to be collected.
          </Empty>
        ) : (
          <div className="space-y-2">
            {awaitingPickup.map((p) => {
              const waitingMin = minutesSince(p.created_at);
              return (
                <SurfaceCard
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {p.therapist?.full_name ?? "Therapist"}
                      {/* Destination names WHICH pickup this is — the same
                          leg that goes to a next client, to the hub, or to
                          her own origin all read identically otherwise. */}
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        ·{" "}
                        {p.destination === "next_client" ? "to next client" : `to ${p.destination}`}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {p.rider?.full_name ? `Rider: ${p.rider.full_name}` : "No rider assigned yet"}
                      {waitingMin > 0 ? ` · waiting ${waitingMin}m` : null}
                    </p>
                  </div>
                </SurfaceCard>
              );
            })}
          </div>
        )}
      </section>

      {/* Queue stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {(
          [
            ["Available", available.length, "text-success"],
            ["On trip", onTrip.length, "text-info"],
            ["Offline", offline.length, "text-muted-foreground"],
          ] as const
        ).map(([label, value, tone]) => (
          <StatTile layout="compact" label={label} icon={ChartBarIcon} key={label}>
            <p className={cn("text-2xl font-semibold tabular-nums", tone)}>
              {isLoading ? "…" : value}
            </p>
          </StatTile>
        ))}
      </div>

      {/* The FIFO itself */}
      <section className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="type-title-subsection">Rider queue</h2>
            <p className="type-body-default text-muted-foreground">FIFO order; assignment always takes the longest-waiting available rider</p>
          </div>
          <span className="type-body-small text-muted-foreground">refreshes every 15s</span>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
            ))}
          </div>
        ) : riders.length === 0 ? (
          <Empty>
            No riders in the queue yet — riders appear here once they start a Rider Session.
          </Empty>
        ) : (
          <ul className="divide-y divide-border/60">
            {riders.map((r, idx) => {
              const meta = QUEUE_STATUS[r.status] ?? QUEUE_STATUS.offline!;
              const waitingMins =
                r.status === "available" && r.available_since
                  ? minutesSince(r.available_since)
                  : null;
              return (
                <li key={r.rider_id} className="flex items-center gap-3 py-2.5">
                  <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {r.status === "available" ? `${idx + 1}` : ""}
                  </span>
                  <span className={cn("size-2.5 shrink-0 rounded-full", meta.dot)} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {r.staff?.full_name ?? "Rider"}
                      {!hubScoped && r.hubs?.name ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {r.hubs.name}
                        </span>
                      ) : null}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {r.status === "assigned" && r.bookings?.ref
                        ? `On ${r.bookings.ref} · ${r.bookings.status ? bookingStatusLabel(r.bookings.status) : ""}`
                        : waitingMins != null
                          ? `waiting ${waitingMins}m`
                          : "—"}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 type-body-micro font-medium",
                      meta.badge,
                    )}
                  >
                    {meta.label}
                  </span>
                  {/* The release refuses a rider already driving to the client or
                      there (0053), so the control is absent rather than certain
                      to fail. A live trip it cannot see from here still refuses
                      — the popup shows that. */}
                  {r.status === "assigned" && !RELEASE_REFUSED.has(r.bookings?.status ?? "") ? (
                    <Button
                      variant="destructive"
                      size="icon-sm"
                      type="button"
                      title="Release this rider — the booking goes back to waiting for a rider"
                      aria-label={`Release ${r.staff?.full_name ?? "rider"}`}
                      onClick={() =>
                        setReleaseTarget({
                          riderId: r.rider_id,
                          name: r.staff?.full_name ?? "Rider",
                        })
                      }
                      disabled={
                        releaseRiderDispatch.isPending &&
                        releaseRiderDispatch.variables?.riderId === r.rider_id
                      }
                      className="shrink-0"
                    >
                      <XCircleIcon />
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Queue stats — therapist side */}
      <div className="grid grid-cols-2 gap-4">
        {(
          [
            ["In today's queue", therapists.length, "text-foreground"],
            ["Currently on a job", therapistsAssigned.length, "text-info"],
          ] as const
        ).map(([label, value, tone]) => (
          <StatTile layout="compact" label={label} icon={ChartBarIcon} key={label}>
            <p className={cn("text-2xl font-semibold tabular-nums", tone)}>
              {therapistQueueLoading ? "…" : value}
            </p>
          </StatTile>
        ))}
      </div>

      {/* The ASAP fairness queue — Flow 13/14 */}
      <section className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="type-title-subsection">Therapist queue</h2>
            <p className="type-body-default text-muted-foreground">fairness order; ranked by yesterday's earnings, lowest first</p>
          </div>
          <span className="type-body-small text-muted-foreground">refreshes every 15s</span>
        </div>

        {therapistQueueLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
            ))}
          </div>
        ) : therapists.length === 0 ? (
          <Empty>No therapists in today's queue yet — they appear here once they clock in.</Empty>
        ) : (
          <ul className="divide-y divide-border/60">
            {therapists.map((t) => {
              // `prior_position`, not `moved_seq` — see the note at the filter above.
              const assigned = t.prior_position !== null;
              const activeBooking = activeBookingByTherapist.get(t.staff_id);
              const minutesLeft = activeBooking ? minutesLeftOnJob(activeBooking) : null;
              return (
                <li key={t.staff_id} className="flex items-center gap-3 py-2.5">
                  <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {t.position}
                  </span>
                  <span
                    className={cn(
                      "size-2.5 shrink-0 rounded-full",
                      assigned ? "bg-info" : "bg-success",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {t.staff?.full_name ?? "Therapist"}
                      {!hubScoped && t.hubs?.name ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {t.hubs.name}
                        </span>
                      ) : null}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {TIER_LABEL[t.tier] ?? t.tier}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 type-body-micro font-medium",
                      assigned ? "bg-info/15 text-info" : "bg-success/15 text-success",
                    )}
                  >
                    {assigned
                      ? minutesLeft !== null
                        ? `On a job (${minutesLeft}m left)`
                        : "On a job"
                      : "Waiting"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ⚠ ALWAYS MOUNTED. Guarding with `releaseTarget && …` unmounted the
          whole tree on close, which kills a Dialog's exit animation — the
          component decides its own open state from a null name instead. */}
      <ReleaseDispatchModal
        riderName={releaseTarget?.name ?? null}
        isPending={releaseRiderDispatch.isPending}
        errorMessage={releaseRiderDispatch.error ? releaseRiderDispatch.error.message : null}
        onConfirm={confirmRelease}
        onClose={() => {
          // A failure belongs to the attempt that made it — never left on the page.
          setReleaseTarget(null);
          releaseRiderDispatch.reset();
        }}
      />
    </div>
  );
}
